/* eslint no-lone-blocks: 0 */
import * as React from 'react';
import * as R from 'ramda';
import moment from 'moment';
import { EventOrValueHandler, Field, FieldArray, WrappedFieldProps } from 'redux-form';

import { Trans } from 'react-i18next';
import { connect, ConnectedProps } from 'react-redux';

import { Select, AsyncSelect } from '@common/components/form/inputs/select';
import { AsyncTable, Table } from '../table';
import Icon, { IconTypes } from '../icon';
import { Button, ButtonType } from '../button';
import Modal from '../modal';
import Label from '../form/label';
import Dropdown from '../dropdown';
import SearchBar from '../search-bar';
import List from '../list';
import ProfileImage from '../profile-image';

import { PredicateSelectorMultiSelect } from './filter-multi-select';
import { PredicateSelectorAsyncMultiSelect } from './filter-async-multi-select';
import { PredicateSelectorDate } from './filter-date';
import { PredicateSelectorNumber } from './filter-number';
import { PredicateSelectorSingleSelect } from './filter-single-select';

import * as organisationSelector from '../../../modules/organisation/selectors/organisation';

import Api from '../../services/api';
import createTempId from '../../utils/create-temp-id';
import mergeNames from '../../utils/merged-names-text';
import type { ListApiResponse } from '@common/types/util-types';
import { predicatesToPayload } from '@common/utils/predicates';

import type { SimpleUser, Organisation, Network, OrganisationFunction } from '../../types/objects';
import type { StoreState } from '@common/types/store';
import type { Predicate } from '@common/types/objects';
import { ESearchPredicateTypes, EPredicateOperators, EPredicateFields } from '@common/definitions';
import { t } from 'i18next';
import LinkWrapper from '../linkWrapper';
import { useAdminScreen } from '@common/hooks/use-admin-screen';
import { useStoredContentHeight } from './use-audience-modal-height';
import {
  emptyAudienceUserSearchResponse,
  buildAudienceModalFilter,
  filterAudienceCommunitiesBySearch,
  findUserInAudienceSearchResults,
  resolveAudienceCommunities,
  resolveAudienceSearchResultI18nKeys,
  shouldResolveCommunitiesFromMatchedUsers,
  shouldSkipEmptyAudienceUserSearch,
  type AudienceSearchResultMode,
} from './audience-user-search';
import { useOrganisationUserPropertyPredicateFilters }
from '@modules/user-properties/hooks/use-organisation-user-property-predicate-filters';
import {
  type CustomUserPropertyPredicateFilter,
} from '@modules/user-properties/utils/build-custom-user-property-predicate-filters';
import { UserPropertiesPredicateSubmenu }
from '@modules/user-properties/components/user-properties-predicate-submenu/user-properties-predicate-submenu';
import {
  isCustomUserPropertyPredicateFilter,
  isPredicateValueEmpty,
  shouldDisablePredicateFilter,
  sortCustomUserPropertyFilters,
} from './predicate-filter-helpers';

// Filters that allow multiple instances
const MULTIPLE_INSTANCE_FILTERS = [
  EPredicateFields.DAYS_IN_SERVICE,
  EPredicateFields.DAYS_BEFORE_OUT_OF_SERVICE,
];

function shouldDisableFilter(filterValue: string, existingFields: string[]): boolean {
  return shouldDisablePredicateFilter(filterValue, existingFields, MULTIPLE_INSTANCE_FILTERS);
}

function PredicateFilterLabel({
  filter,
}: {
  filter: PredicateFilter | CustomUserPropertyPredicateFilter;
}) {
  if (isCustomUserPropertyPredicateFilter(filter)) {
    return <>{filter.label}</>;
  }

  return (
    <Trans
      i18nKey="common:predicates_filter_predicate"
      values={{ context: filter.context }}
    />
  );
}

// Display user result
export const PredicatesUserItem = ({ item: user, maxShownItems = 3, baseUrl = '/admin' }:
  { item: SimpleUser, maxShownItems?: number, baseUrl?: string }) => [
    <LinkWrapper url={`${baseUrl}/users/${user.id}/about`} target="_blank">
      <ProfileImage enablePopover key="profile_img" size={35} user={user} />
    </LinkWrapper>,
    <LinkWrapper url={`${baseUrl}/users/${user.id}/about`} target="_blank">
      <span key="full_name" className="fs-exclude">{user.full_name}</span>
    </LinkWrapper>,
    // @ts-expect-error
    <span key="networks">{mergeNames(user.networks, maxShownItems, 'name', undefined, '+')}</span>,
    // @ts-expect-error
    <span key="functions">{mergeNames(user.functions, maxShownItems, 'name', undefined, '+')}</span>,
  ];

// Display community result — the row a per-community task's audience resolves to
export const PredicatesCommunityItem = ({ item: network }: { item: Network }) => [
  <span key="name" className="fs-exclude">{network.name}</span>,
  <span key="member_count">{network.member_count ?? 0}</span>,
];

type ItemProps = {
  item: Record<string, unknown>,
  labelKey: string,
  valueKey: string,
  defaultText: string,
  renderRow?: ({ item }: { item: Record<string, unknown> }) => React.ReactNode,
  onRemove: (value: unknown) => void,
};

// Item in predicate filter
const MultiInputItem = React.memo<ItemProps>((props: ItemProps) => {
  const {
    item,
    labelKey,
    valueKey,
    defaultText,
    renderRow,
    onRemove,
    ...rest
  } = props;
  return (
    <div className="Form__MultiInput__Item">
      {renderRow ? renderRow({ item, ...rest }) : item[labelKey]}
      {
        item.default ?
          <small>{defaultText}</small> :
          <Button
            icon="delete"
            size="large"
            onClick={() => onRemove(item[valueKey])}
          />
      }
    </div>
  );
});

type Option = Record<string, unknown>;

type MultiInputValue = Array<{ value: any, label: string }>;

type MultiInputOwnProps = {
  value: MultiInputValue;
  options?: Option[];
  labelKey?: string;
  valueKey?: string;
  defaultSelected?: Option[];
  searchPlaceholder?: string;
  defaultText?: string;
  className?: string;
  placeholder?: string;
  getOptions?: (value: string) => Promise<Option[]>;
  renderRow?: ({ item }: { item: Option }) => React.ReactNode;
  disabled?: boolean;
  onChange: EventOrValueHandler<React.ChangeEvent<MultiInputValue>>;
  hideSelectedItems?: boolean;
};

export const MultiInput = (props: MultiInputOwnProps) => {
  const {
    value,
    onChange,
    options,
    defaultSelected,
    getOptions,
    renderRow,
    placeholder,
    valueKey,
    className,
    labelKey,
    disabled = false,
    /** When true, the selected-items list (chips + remove) is hidden. Default false shows it. */
    hideSelectedItems = false,
    ...rest
  } = props;

  const selected = defaultSelected
    ? [...defaultSelected.map(R.assoc('default', true)), ...value]
    : value;

  // Fetch data async or data is already loaded
  const Selector = getOptions ? AsyncSelect : Select;

  // If item is already selected, disable it in the selector
  const disableNonAvailableOptions: (items: Option[]) => Option[] = R.map((item) => {
    // @ts-expect-error
    const find = R.find(R.propEq(valueKey, item[valueKey]), selected.filter((o) => !!o));

    if (find) return R.assoc('disabled', true, item);

    return item;
  });

  const addToFilter = (option: Option) => onChange(R.append(option, value));
  // @ts-expect-error
  const removeFromFilter = (id: string) => onChange(R.reject(R.propEq(valueKey, id), value));

  // react-select onChange can pass a single option, an array (multi), or null
  const handleChange = (newValue: Option | Option[] | null) => {
    if (!newValue) return;
    if (Array.isArray(newValue)) {
      newValue.forEach((opt) => addToFilter(opt));
    } else {
      addToFilter(newValue);
    }
  };

  return (
    <div className={`Form__MultiInput${className ? ` ${className}` : ''}`}>
      <Selector
        searchable
        options={options && disableNonAvailableOptions(options)}
        getOptions={getOptions && ((searchQuery) => getOptions(searchQuery)
          .then(disableNonAvailableOptions))}
        labelKey={props.labelKey}
        valueKey={props.valueKey}
        placeholder={props.searchPlaceholder}
        onChange={handleChange}
        disabled={disabled}
      />
      {!hideSelectedItems && <List
        // @ts-expect-error
        items={selected || []}
        renderRow={MultiInputItem}
        rowProps={{
          key: valueKey,
          renderRow,
          valueKey,
          labelKey,
          defaultText: props.defaultText,
          onRemove: removeFromFilter,
          ...rest
        }}
        placeholder={placeholder && <small>{placeholder}</small>}
      />}
    </div>
  );
};

export type PredicateFilter = {
  value: EPredicateFields | string;
  defaultOperator: EPredicateOperators;
  defaultValue: unknown;
  icon: string;
  context: string;
  category: string;
  label?: string;
  isCustomUserProperty?: boolean;
  propertyId?: string;
  propertyType?: string;
  optionsKey?: string;
  valueKey?: string;
  labelKey?: string;
  selector: any;
  previewer: PredicatePreviewerFunc;
  previewText?: string;
  iconColor?: string;
  iconSize?: number;
  getData?: (...args: any[]) => any;
  getOptions?: (...args: any[]) => any;
};

export type PredicateSelectFunc<Config = {}> = (
  predicate: Predicate,
  onChange: (newPredicate: Partial<Predicate>) => void,
  config: Config,
) => React.ReactNode;

export type PredicatePreviewerFunc<T extends {} = {}> = (
  value: any,
  filter: T,
  options?: Option[] | undefined,
  previewText?: string,
  hideDefaultText?: boolean,
) => React.ReactNode;

export type PredicateFilterItem = {
  value: any,
  filter: Record<string, any>,
  options?: Option[] | undefined,
};

export const optionsPreviewer: PredicatePreviewerFunc<{ labelKey: string, valueKey: string, hideDefaultText?: boolean }> = (
  { value },
  { labelKey = 'name', valueKey = 'id', hideDefaultText = false },
  options,
) => {
  if (value.length === 0 && !hideDefaultText) {
    return (
      <span>
        <Trans i18nKey="common:predicates_filter_in" />
        {' '}
        <Trans i18nKey="common:predicates_filter_any" />
      </span>
    );
  }

  // @ts-expect-error
  const items = value.map((id: string) => R.find(R.propEq(valueKey, id), options)).filter((option: Option) => !!option);
  const first = items.shift();

  // If there are items but they can't be found in the data
  if (!first) return;

  return (
    <span>
      {!hideDefaultText && <Trans i18nKey="common:predicates_filter_in" />}
      {' '}
      {first[labelKey]}
      {items.length > 0 ? ` +${items.length}` : ''}
    </span>
  );
};

export const singleOptionsPreviewer: PredicatePreviewerFunc<{ labelKey: string, valueKey: string, previewText?: string }> = (
  { value },
  { labelKey = 'name', valueKey = 'id', previewText },
  options,
) => {
  if (!value || value === '' || value.length === 0) {
    return (
      <span>
        {/* <Trans i18nKey="common:predicates_filter_is" /> */}
        {/* {' '} */}
        {/* <Trans i18nKey="common:predicates_filter_any" /> */}
      </span>
    );
  }

  const selectedOption = options?.find((option: Option) => option[valueKey] === value);


  // If the selected option can't be found in the data
  if (!selectedOption) return;

  return (
    <span>
      {previewText}
      {/* <Trans i18nKey="common:predicates_filter_is" /> */}
      {' '}
      {selectedOption[labelKey]}
    </span>
  );
};

export const numberPreviewer: PredicatePreviewerFunc = ({ comparison, value }) => (
  <span>
    <Trans
      i18nKey="common:predicates_filter_comparison"
      values={{
        context: comparison,
        value: value[0] !== undefined ? value[0] : value,
        otherValue: value[1],
      }}
    />
  </span>
);

export const datePreviewer: PredicatePreviewerFunc = ({ value }) => {
  const [from, to] = value;
  if (from === null || to === null) {
    return (
      <span>
        <Trans i18nKey="common:predicates_filter_in" />
        {' '}
        <Trans i18nKey="common:predicates_filter_any" />
      </span>
    );
  }

  return (
    <span>
      <Trans
        i18nKey="common:predicates_filter_comparison"
        values={{
          context: 'between',
          value: moment(value[0]).format('DD-MM-YYYY'),
          otherValue: moment(value[1]).format('DD-MM-YYYY'),
        }}
      />
    </span>
  );
};

export const predicateTypes = [
  ESearchPredicateTypes.MATCH_ALL,
  ESearchPredicateTypes.MATCH_ANY,
];

export const EPredicateFilters: Record<string, any> = {
  [EPredicateFields.USER]: {
    value: EPredicateFields.USER,
    defaultOperator: EPredicateOperators.IN,
    defaultValue: [],
    icon: 'person',
    context: 'user_name',
    category: 'employee',
    selector: PredicateSelectorAsyncMultiSelect,
    previewer: optionsPreviewer,
    getOptions: (organisationId: string, search: string) => {
      if (!search) return [];

      const query = Api.utils.toQuery({ q: search, limit: 25 });

      return Api.get(`/v2/organisations/${organisationId}/users?${query}`)
        .then((result) => result.data);
    },
    getData: (value: any, state: any, searchResult: any) => {
      return value.map((userId: string) => {
        return (
          state.users.items[userId] ||
          findUserInAudienceSearchResults(searchResult?.data, userId)
        );
      });
    },
    labelKey: 'full_name',
  },
  [EPredicateFields.LANGUAGE]: {
    value: EPredicateFields.LANGUAGE,
    defaultOperator: EPredicateOperators.IN,
    defaultValue: [],
    icon: 'language',
    context: 'language',
    category: 'employee',
    selector: PredicateSelectorMultiSelect,
    previewer: optionsPreviewer,
    optionsKey: 'languageLocales',
    valueKey: 'locale',
    labelKey: 'name_eng',
  },
  [EPredicateFields.DAYS_IN_SERVICE]: {
    value: EPredicateFields.DAYS_IN_SERVICE,
    defaultOperator: EPredicateOperators.EQ,
    defaultValue: 0,
    icon: 'calendar_today',
    context: 'days_in_service',
    category: 'employee',
    selector: PredicateSelectorNumber,
    previewer: numberPreviewer,
  },
  [EPredicateFields.DAYS_BEFORE_OUT_OF_SERVICE]: {
    value: EPredicateFields.DAYS_BEFORE_OUT_OF_SERVICE,
    defaultOperator: EPredicateOperators.EQ,
    defaultValue: 0,
    icon: 'event_busy',
    context: 'days_before_out_of_service',
    category: 'employee',
    selector: PredicateSelectorNumber,
    previewer: numberPreviewer,
  },
  [EPredicateFields.NETWORK]: {
    value: EPredicateFields.NETWORK,
    defaultOperator: EPredicateOperators.IN,
    defaultValue: [],
    icon: 'business',
    context: 'network',
    category: 'company',
    selector: PredicateSelectorMultiSelect,
    previewer: optionsPreviewer,
    optionsKey: 'networks',
  },
  [EPredicateFields.FUNCTION]: {
    value: EPredicateFields.FUNCTION,
    defaultOperator: EPredicateOperators.IN,
    defaultValue: [],
    icon: 'assignment_ind',
    context: 'function',
    category: 'company',
    selector: PredicateSelectorMultiSelect,
    previewer: optionsPreviewer,
    optionsKey: 'functions',
  },
  [EPredicateFields.EMPLOYEE_MANAGER]: {
    value: EPredicateFields.EMPLOYEE_MANAGER,
    defaultOperator: EPredicateOperators.IN,
    defaultValue: [],
    icon: 'supervisor_account',
    context: 'employee_manager',
    category: 'employee',
    selector: PredicateSelectorMultiSelect,
    previewer: optionsPreviewer,
    optionsKey: 'managers',
    valueKey: 'id',
    labelKey: 'full_name',
  },
  [EPredicateFields.DATE]: {
    value: EPredicateFields.DATE,
    defaultOperator: EPredicateOperators.EQ,
    defaultValue: null,
    icon: 'date_start',
    context: 'date',
    category: 'calendar',
    selector: PredicateSelectorDate,
    previewer: datePreviewer,
  },
  [EPredicateFields.PERFORMANCE_FORM_TYPE]: {
    value: EPredicateFields.PERFORMANCE_FORM_TYPE,
    defaultOperator: EPredicateOperators.EQ,
    defaultValue: '',
    icon: 'settings__filled',
    context: 'performance_form_type',
    category: 'performance',
    selector: PredicateSelectorSingleSelect,
    previewer: singleOptionsPreviewer,
    valueKey: 'value',
    labelKey: 'label',
    iconColor: 'grey-dull',
    iconSize: 18,
    isSearchable: false,
  },
  [EPredicateFields.PERFORMANCE_FORM_STATUS]: {
    value: EPredicateFields.PERFORMANCE_FORM_STATUS,
    defaultOperator: EPredicateOperators.EQ,
    defaultValue: '',
    icon: 'check_tick_circle',
    context: 'performance_form_status',
    category: 'performance',
    selector: PredicateSelectorSingleSelect,
    previewer: singleOptionsPreviewer,
    valueKey: 'value',
    labelKey: 'label',
    iconColor: 'grey-dull',
    iconSize: 18,
    isSearchable: false,
  },
  [EPredicateFields.COMPETENCY_GROUP_BY]: {
    value: EPredicateFields.COMPETENCY_GROUP_BY,
    defaultOperator: EPredicateOperators.EQ,
    defaultValue: '',
    icon: 'swap_verticle',
    context: 'competency_group_by',
    category: 'competency',
    selector: PredicateSelectorSingleSelect,
    previewer: singleOptionsPreviewer,
    valueKey: 'value',
    labelKey: 'label',
    isSearchable: false,
  },
  [EPredicateFields.COMPETENCY_LANGUAGE]: {
    value: EPredicateFields.COMPETENCY_LANGUAGE,
    defaultOperator: EPredicateOperators.EQ,
    defaultValue: '',
    icon: 'language',
    context: 'competency_language',
    category: 'competency',
    selector: PredicateSelectorSingleSelect,
    previewer: singleOptionsPreviewer,
    optionsKey: 'language',
    valueKey: 'locale',
    labelKey: 'name_eng',
  },
  [EPredicateFields.PERFORMANCE_FORM_CREATED_BY]: {
    value: EPredicateFields.PERFORMANCE_FORM_CREATED_BY,
    defaultOperator: EPredicateOperators.EQ,
    defaultValue: '',
    icon: 'person',
    context: 'created_by',
    category: 'competency',
    selector: PredicateSelectorSingleSelect,
    previewer: singleOptionsPreviewer,
    valueKey: 'id',
    labelKey: 'full_name',
    iconColor: 'grey-dull',
    iconSize: 18,
  },
  [EPredicateFields.PERFORMANCE_EVALUATION_STATUS]: {
    value: EPredicateFields.PERFORMANCE_EVALUATION_STATUS,
    defaultOperator: EPredicateOperators.EQ,
    defaultValue: '',
    icon: 'check_tick_circle',
    context: 'performance_evaluation_status',
    category: 'performance',
    selector: PredicateSelectorSingleSelect,
    previewer: singleOptionsPreviewer,
    previewText: t('common:predicates_filter_is'),
    valueKey: 'value',
    labelKey: 'label',
    isSearchable: false,
    isSortable: false,
  },
  [EPredicateFields.PERFORMANCE_EVALUATION_MANAGER]: {
    value: EPredicateFields.PERFORMANCE_EVALUATION_MANAGER,
    defaultOperator: EPredicateOperators.IN,
    defaultValue: [],
    icon: 'person',
    context: 'performance_evaluation_manager',
    category: 'performance',
    selector: PredicateSelectorMultiSelect,
    previewer: optionsPreviewer,
    optionsKey: 'users',
    valueKey: 'id',
    labelKey: 'full_name',
  },
  [EPredicateFields.CALENDAR_EVENT_TYPE]: {
    value: EPredicateFields.CALENDAR_EVENT_TYPE,
    defaultOperator: EPredicateOperators.IN,
    defaultValue: [],
    icon: 'calendar_today',
    context: 'calendar_event_type',
    category: 'calendar',
    selector: PredicateSelectorMultiSelect,
    previewer: optionsPreviewer,
    optionsKey: 'filteredOptions',
    valueKey: 'id',
    labelKey: 'label',
    isSearchable: false,
    checkAll: false,
    hideDefaultText: true,
    isSortable: false,
  },
  [EPredicateFields.COMPETENCY_CATEGORY]: {
    value: EPredicateFields.COMPETENCY_CATEGORY,
    defaultOperator: EPredicateOperators.IN,
    defaultValue: [],
    icon: 'briefcase_job',
    context: 'competency_category',
    category: 'competency',
    selector: PredicateSelectorMultiSelect,
    previewer: optionsPreviewer,
    optionsKey: 'categories',
    valueKey: 'id',
    labelKey: 'name',
    isSearchable: false,
    isSortable: false,
  },
  [EPredicateFields.GRID_KEY]: {
    value: EPredicateFields.GRID_KEY,
    defaultOperator: EPredicateOperators.IN,
    defaultValue: [],
    icon: 'grid_table_view',
    context: 'grid_key',
    category: 'performance',
    selector: PredicateSelectorMultiSelect,
    previewer: optionsPreviewer,
    optionsKey: 'gridKeyOptions',
    valueKey: 'value',
    labelKey: 'label',
    isSearchable: false,
    isSortable: false,
  },
  [EPredicateFields.PERFORMANCE_EVALUATION_FORM]: {
    value: EPredicateFields.PERFORMANCE_EVALUATION_FORM,
    defaultOperator: EPredicateOperators.IN,
    defaultValue: [],
    icon: 'file_text__filled',
    context: 'performance_evaluation_form',
    category: 'performance',
    selector: PredicateSelectorMultiSelect,
    previewer: optionsPreviewer,
    optionsKey: 'forms',
    valueKey: 'id',
    labelKey: 'title',
    isSortable: true,
  },
  [EPredicateFields.TASK_STATUS]: {
    value: EPredicateFields.TASK_STATUS,
    defaultOperator: EPredicateOperators.IN,
    defaultValue: [],
    icon: 'check_tick_circle',
    context: 'task_status',
    category: 'tasks',
    selector: PredicateSelectorMultiSelect,
    previewer: optionsPreviewer,
    valueKey: 'value',
    labelKey: 'label',
    iconColor: 'grey-dull',
    iconSize: 18,
    isSearchable: false,
    hideDefaultText: true,
    isSortable: false,
  },
  [EPredicateFields.TASK_TYPE]: {
    value: EPredicateFields.TASK_TYPE,
    defaultOperator: EPredicateOperators.EQ,
    defaultValue: '',
    icon: 'settings__filled',
    context: 'task_type',
    category: 'tasks',
    selector: PredicateSelectorSingleSelect,
    previewer: singleOptionsPreviewer,
    valueKey: 'value',
    labelKey: 'label',
    iconColor: 'grey-dull',
    iconSize: 18,
    isSearchable: false,
  },
  [EPredicateFields.TASK_FREQUENCY]: {
    value: EPredicateFields.TASK_FREQUENCY,
    defaultOperator: EPredicateOperators.EQ,
    defaultValue: '',
    icon: 'calendar_timer',
    context: 'task_frequency',
    category: 'tasks',
    selector: PredicateSelectorSingleSelect,
    previewer: singleOptionsPreviewer,
    valueKey: 'value',
    labelKey: 'label',
    iconColor: 'grey-dull',
    iconSize: 18,
    isSearchable: false,
  },
  [EPredicateFields.FORM_STATUS]: {
    value: EPredicateFields.FORM_STATUS,
    defaultOperator: EPredicateOperators.IN,
    defaultValue: [],
    icon: 'check_tick_circle',
    context: 'form_status',
    category: 'forms',
    selector: PredicateSelectorMultiSelect,
    previewer: optionsPreviewer,
    valueKey: 'value',
    labelKey: 'label',
    iconColor: 'grey-dull',
    iconSize: 18,
    isSearchable: false,
    hideDefaultText: true,
    isSortable: false,
  },
};

export type PredicateInputOwnProps = {
  input: {
    value: any,
    onChange: (newValue: any) => void,
  },
  filter: PredicateFilter;
  options: Option[];
  organisation: Organisation;
  onRemove?: () => void;
  addContextToPredicatesOnChange: boolean;
  className?: string;
  scrolling?: boolean;
  buttonType?: ButtonType;
  readOnly?: boolean;
};

// Single dropdown for a predicate
export const PredicateInput = ({
  input: {
    value: predicate,
    onChange,
  },
  filter,
  options,
  organisation,
  onRemove,
  addContextToPredicatesOnChange,
  className,
  scrolling = true,
  buttonType = 'default',
  readOnly = false,
}: PredicateInputOwnProps) => {
  const [isOpen, setOpen] = React.useState(!!predicate.open);
  const [selectedOptions, setSelectedOptions] = React.useState(options);
  const valueKey = filter.valueKey || 'id';
  const filterGetOptions = (filter as { getOptions?: unknown }).getOptions;

  // For async filters (e.g. the user predicate) the selected options are tracked
  // in local state so user-added entries that are not in the redux store survive
  // re-renders. The `options` prop, however, can grow *after* mount when the
  // selected entries are resolved lazily (e.g. fetched in a parent effect and
  // injected into the store). Merge any newly resolved options in so the preview
  // and dropdown reflect the full selection instead of only what was available on
  // the first render. This is purely additive, so it never drops user-added items.
  React.useEffect(() => {
    if (!filterGetOptions || !Array.isArray(options) || options.length === 0) return;
    setSelectedOptions((prev) => {
      const prevList = Array.isArray(prev) ? prev : [];
      const seen = new Set(prevList.map((o: any) => o?.[valueKey]));
      const additions = options.filter((o: any) => {
        if (!o) return false;
        const key = o[valueKey];
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      return additions.length ? [...prevList, ...additions] : prevList;
    });
  }, [options, valueKey, filterGetOptions]);

  const preview = filter.previewer?.(predicate, filter, filter.getOptions ? selectedOptions : options);

  React.useEffect(() => {
    if (predicate.open) onChange(R.omit(['open'], predicate));
  }, []);

  const FilterComponent = filter.selector;

  return (
    <Dropdown
      className={`${filter.value} ${className || ''}`}
      small
      defaultOpen={!!predicate.open}
      onOpen={() => setOpen(true)}
      onClose={() => {
        setOpen(false);

        // Remove predicates which are "empty"
        if (onRemove && isPredicateValueEmpty(predicate.value, filter)) {
          if (filter.context === 'question' && predicate.id) {
            return;
          }
          return onRemove();
        }
      }}
      scrolling={scrolling}
      disabled={readOnly}
      toggle={(
        <Button
          type={buttonType} size="large" active={isOpen}
          icon={filter.icon as IconTypes}
          iconColor={filter.iconColor}
          iconSize={18}
          className="tw-flex tw-items-center"
          backgroundColor="!tw-bg-gray-custom-200 !tw-text-gray-custom-750"
          disabled={readOnly}
        >
          <PredicateFilterLabel filter={filter} />
          {preview && (
            <span className="tw-font-normal">
              &nbsp;
              {preview}
            </span>
          )}
          {!readOnly && isOpen && onRemove && filter.value !== EPredicateFields.COMPETENCY_GROUP_BY && (
            <Icon
              type="close"
              className="PredicatesFilter__Filters__Remove tw-cursor-pointer"
              onPointerDownCapture={(e: React.PointerEvent) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              onClickCapture={(e: React.MouseEvent) => {
                e.stopPropagation();
                e.preventDefault();
                onRemove();
                setOpen(false);
              }}
            />
          )}
        </Button>
      )}
      search={FilterComponent === PredicateSelectorAsyncMultiSelect}
    >
      <FilterComponent
        valueKey="id"
        labelKey="name"
        predicate={predicate}
        organisation={organisation}
        updateSelectedOptions={setSelectedOptions}
        options={filter.getOptions ? selectedOptions : options}
        {...filter}
        onChange={(newValue: any) => {
          try {
            const newPredicates: any = R.merge(predicate, newValue);
            if (addContextToPredicatesOnChange) {
              const opts = filter.getOptions ? selectedOptions : options;
              let context: any;
              if (Array.isArray(newPredicates.value)) {
                context = newPredicates.value.map((id: string) => {
                  return opts.find((option: any) => option.id === id);
                }).filter((option: any) => !!option);
              } else {
                context = newPredicates.value;
              }
              newPredicates.context = context;
            }
            return onChange(newPredicates);
          } catch (error) {
            console.log('debug error', error);
            throw error;
          }
        }}
      />
    </Dropdown>
  );
};

type PredicateListProps = {
  fields: {
    getAll: () => Array<Object>,
    remove: (index: number) => void,
    push: (value: Object) => void,
    name: string,
  },
  predicateType: string,
  data: Object,
  enabledFilters: string[],
  customPropertyFilters: Record<string, CustomUserPropertyPredicateFilter>,
  userPropertyFilters: CustomUserPropertyPredicateFilter[],
  onAddFilterClick?: () => boolean;
  onFilterSelect?: (type: string) => boolean;
  getState: () => StoreState,
  organisation: Organisation,
  addContextToPredicatesOnChange: boolean;
  readOnly?: boolean;
  lockedFilters?: string[];
};

// List of all predicate dropdowns
const PredicatesList = ({
  fields,
  predicateType,
  data,
  enabledFilters,
  customPropertyFilters,
  userPropertyFilters,
  getState,
  organisation,
  // @ts-expect-error
  searchResult,
  addContextToPredicatesOnChange,
  onAddFilterClick = () => true,
  onFilterSelect = () => true,
  readOnly = false,
  lockedFilters = [],
}: PredicateListProps) => {
  const resolvedFilters = React.useMemo(
    () => ({ ...EPredicateFilters, ...customPropertyFilters }),
    [customPropertyFilters]
  );

  const filters = R.pipe(
    // @ts-expect-error
    R.map((key) => EPredicateFilters[key]),
    R.groupBy(R.prop('category')),
  )(enabledFilters);

  const predicateTypeLabel = t(
    predicateType === ESearchPredicateTypes.MATCH_ALL
      ? 'common:utils_predicates_and'
      : 'common:utils_predicates_or'
  );

  const elems: any[] = [];

  fields.getAll().forEach((predicate: any, index: number) => {
    if (index > 0) {
      const attr = predicate.attribute;
      elems.push(
        <div
          className={`PredicatesFilter__Filters__PredicateType before-${attr}`}
          key={`${predicate.id}_type`}
        >
          {predicateTypeLabel}
        </div>
      );
    }

    const filter = resolvedFilters[predicate.attribute];
    if (!filter || (filter.getData && !searchResult && !isCustomUserPropertyPredicateFilter(filter))) {
      return null;
    }

    const optionsData = filter.getData
      ? filter.getData(predicate.value, getState(), searchResult)
      : // @ts-expect-error
        filter.optionsKey && data[filter.optionsKey];

    // this fixed PD-8191 for all predicate filters types
    const options = optionsData?.filter((o: any) => !!o) || [];

    elems.push(
      <Field
        className={`order_${index}`}
        key={predicate.id}
        name={`${fields.name}[${index}]`}
        filter={filter}
        options={options}
        getState={getState}
        organisation={organisation}
        onRemove={
          readOnly || lockedFilters.includes(predicate.attribute)
            ? undefined
            : () => fields.remove(index)
        }
        addContextToPredicatesOnChange={addContextToPredicatesOnChange}
        readOnly={readOnly}
        component={PredicateInput}
      />
    );
  });

  const existingFields = fields.getAll().map((predicate: any) => predicate.attribute);

  const handleAddCustomPropertyFilter = (filter: CustomUserPropertyPredicateFilter) => {
    fields.push({
      id: createTempId(),
      open: true,
      attribute: filter.value,
      comparison: filter.defaultOperator,
      value: filter.defaultValue,
    });
  };

  return (
    <>
      {elems}

      {!readOnly && (
        <Dropdown
          small
          onClick={onAddFilterClick}
          toggle={(
            <Button type="transparent" iconRight="add" size="large">
              <Trans i18nKey="common:predicates_filter_add_filter" />
            </Button>
          )}
        >
          {R.values(R.mapObjIndexed((category: any[], key: string) => {
            // @ts-expect-error
            const handleAddPredicate = (filter) => fields.push({
              id: createTempId(),
              open: true,
              attribute: filter.value,
              comparison: filter.defaultOperator,
              value: filter.defaultValue,
            });

            return (
              <React.Fragment key={key}>
                <Dropdown.Header className="header">
                  <Trans
                    i18nKey="common:predicates_filter_category"
                    values={{ context: key }}
                  />
                </Dropdown.Header>
                {category.map((filter: any) => (
                  <Dropdown.Item
                    className={`${filter.value}_option`}
                    key={filter.value}
                    onClick={() => onFilterSelect(filter.value) && handleAddPredicate(filter)}
                    disabled={shouldDisableFilter(filter.value, existingFields)}
                  >
                    <Icon type={filter.icon} />
                    <Trans
                      i18nKey="common:predicates_filter_predicate"
                      values={{ context: filter.context }}
                    />
                  </Dropdown.Item>
                ))}
                {key === 'employee' && userPropertyFilters.length > 0 && (
                  <UserPropertiesPredicateSubmenu
                    filters={userPropertyFilters}
                    existingFields={existingFields}
                    onFilterSelect={onFilterSelect}
                    onAddFilter={handleAddCustomPropertyFilter}
                  />
                )}
              </React.Fragment>
            );
          }, filters))}
        </Dropdown>
      )}
    </>
  );
};

const defaultFilters = [
  EPredicateFields.USER,
  EPredicateFields.LANGUAGE,
  EPredicateFields.DAYS_IN_SERVICE,
  EPredicateFields.NETWORK,
  EPredicateFields.FUNCTION,
  EPredicateFields.DAYS_BEFORE_OUT_OF_SERVICE
];

type AudienceModalProps = {
  children: React.ReactNode;
};

const getStateGetter = (state: StoreState, props: any) => {
  // this getInjectedState allows us to create a fake redux state so we
  // can provide custom data to the component instead of relying on redux
  // state
  if (props.getInjectedState) {
    return () => props.getInjectedState(state);
  }
  // previous solution to problem explained above, but it forces us to extract
  // the redux state in the parent components and have it consumed by
  // props.getState
  if (props.getState) {
    return props.getState;
  }

  // this just returns the state, so the AudienceInput will just read the
  // actual redux data
  return () => state;
};

const mapStateToProps = (state: StoreState, props: any) => {
  const getState = getStateGetter(state, props);
  const possiblyInjectedReduxState = getState();
  const locales = possiblyInjectedReduxState.language.translations;
  return {
    organisation: organisationSelector.selected(possiblyInjectedReduxState),
    networks: organisationSelector.networks(possiblyInjectedReduxState),
    functions: organisationSelector.functions(possiblyInjectedReduxState),
    languageLocales: locales.map((value: any) => {
      return { ...value, id: value.locale };
    }),
    getState
  };
};

const mapDispatchToProps = {};

const predicatesFilterConnector = connect(mapStateToProps, mapDispatchToProps);

type PredicatesFilterConnectedProps = ConnectedProps<typeof predicatesFilterConnector>;

export type PredicatesFilterOwnProps = {
  onAddFilterClick?: () => boolean;
  onFilterSelect?: (type: string) => boolean;
  /** When true, renders the full UI but prevents any audience edits */
  readOnly?: boolean;
  /** Predicate attributes that stay on the audience and cannot be removed. */
  lockedFilters?: string[];
  /** When true (default), loads org user properties as audience filters */
  enableUserPropertyFilters?: boolean;
  formValues?: {
    predicate_type: ESearchPredicateTypes;
    filters: {
      networks: Network[];
      functions: OrganisationFunction[];
      users: SimpleUser[];
    };
  };
  getSearchResults?: (result: Record<string, unknown>) => void;
  description?: any;
  enabledFilters?: string[];
  tooltips: {
    audience?: string;
  };
  addContextToPredicatesOnChange?: boolean;
  /** Optional validation error message shown below the filter row */
  fieldError?: string;
  /**
   * When false, empty audience predicates do not call org-wide `/users/search`
   * (network-scoped form permissions). Defaults to true.
   */
  allowOrganisationWideAudienceSearch?: boolean;
  /**
   * Hides the inline results table, leaving the filters, the match count and
   * the "View all" link that opens the same table in a modal. The Tasks
   * assignment block uses this (ONE-4829).
   *
   * The table still mounts — it is what fetches the results and reports the
   * count back through `onPostFetch` — it is just not shown.
   */
  hideResultsTable?: boolean;
  /** Hides the "Who will receive this" label above the filter row. */
  hideLabel?: boolean;
  /**
   * `communities` switches the match count, the "View all" list and their copy
   * from employees to the communities a per-community team task opens an
   * instance in (ONE-4929). The `/users/search` request itself is unchanged —
   * its total still decides whether the audience matches anybody at all.
   */
  audienceSearchResultMode?: AudienceSearchResultMode;
};

type PredicateFilterApiResponse = ListApiResponse<
  Record<string, unknown>[],
  undefined,
  { pagination: { total_count: number } }
>;

type Props = WrappedFieldProps & PredicatesFilterConnectedProps & PredicatesFilterOwnProps;

export type PredicatesFilterValue = {
  search: string;
  predicate_type: ESearchPredicateTypes;
  predicates: Predicate[];
};

const PredicatesFilterComponent = ({
  input: {
    value: audience,
    name,
  },
  organisation,
  onAddFilterClick,
  onFilterSelect,
  getSearchResults,
  description,
  enabledFilters = defaultFilters,
  getState,
  tooltips = {},
  addContextToPredicatesOnChange = false,
  fieldError,
  readOnly = false,
  lockedFilters,
  enableUserPropertyFilters = true,
  allowOrganisationWideAudienceSearch = true,
  hideResultsTable = false,
  hideLabel = false,
  audienceSearchResultMode = 'employees',
  ...data
}: Props) => {
  const { filtersMap: customPropertyFilters } = useOrganisationUserPropertyPredicateFilters(
    organisation?.id,
    enableUserPropertyFilters
  );
  const userPropertyFilters = React.useMemo(
    () => sortCustomUserPropertyFilters(Object.values(customPropertyFilters)),
    [customPropertyFilters]
  );
  const [searchResult, setSearchResult] = React.useState<PredicateFilterApiResponse | null>(null);
  const [communityResolutionUsers, setCommunityResolutionUsers] = React.useState<
    PredicateFilterApiResponse['data'] | undefined
  >();
  const [showAudienceModal, setShowAudienceModal] = React.useState(false);
  const isAdminScreen = useAdminScreen();
  const baseUrl = isAdminScreen ? '/admin' : '';
  const { minHeight: inlineMinHeight, ref: inlineContentRef } = useStoredContentHeight(true);
  const columns = [
    { size: 35 },
    { label: t('common:predicates_filter_column_name_label'), className: 'Table__Cell__Title' },
    { label: t('common:predicates_filter_column_communities_label') },
    { label: t('common:predicates_filter_column_functions_label') },
  ];
  const communityColumns = [
    {
      label: t('common:predicates_filter_column_community_label'),
      className: 'Table__Cell__Title',
    },
    { label: t('common:predicates_filter_column_members_label') },
  ];

  const audienceSearchResultI18nKeys = React.useMemo(
    () => resolveAudienceSearchResultI18nKeys(audienceSearchResultMode),
    [audienceSearchResultMode],
  );

  // Employees are what `/users/search` counts; a per-community task opens one
  // instance per community instead, so that mode counts — and lists — the
  // communities the audience names rather than the people inside them.
  const isCommunityAudienceMode = audienceSearchResultMode === 'communities';
  const matchedEmployeeCount = searchResult?.meta?.pagination?.total_count ?? 0;
  const organisationCommunities = (data as { networks?: Network[] }).networks;
  const audienceCommunities = React.useMemo(
    () => (isCommunityAudienceMode
      ? resolveAudienceCommunities(
        audience?.predicates,
        organisationCommunities,
        matchedEmployeeCount,
        communityResolutionUsers,
      )
      : []),
    [
      isCommunityAudienceMode,
      audience?.predicates,
      organisationCommunities,
      matchedEmployeeCount,
      communityResolutionUsers,
    ],
  );
  const audienceSearchResultCount = isCommunityAudienceMode
    ? audienceCommunities.length
    : matchedEmployeeCount;

  const onFetch = (offset: number | undefined, filter: PredicatesFilterValue, limit = 10) => {
    if (
      shouldSkipEmptyAudienceUserSearch(
        allowOrganisationWideAudienceSearch,
        filter.predicates
      )
    ) {
      return Promise.resolve(emptyAudienceUserSearchResponse(limit));
    }

    const query = Api.utils.toQuery({
      offset,
      limit,
      include: ['networks', 'functions'],
      q: filter.search,
    });

    return Api.post(`/v1/organisations/${organisation.id}/users/search?${query}`, {
      predicate_type: filter.predicate_type,
      predicates: predicatesToPayload(filter.predicates),
    });
  };

  React.useEffect(() => {
    if (
      !isCommunityAudienceMode ||
      !searchResult ||
      !shouldResolveCommunitiesFromMatchedUsers(audience?.predicates)
    ) {
      setCommunityResolutionUsers(undefined);
      return undefined;
    }

    const total = searchResult.meta.pagination.total_count;
    const loadedCount = searchResult.data?.length ?? 0;

    if (loadedCount >= total) {
      setCommunityResolutionUsers(searchResult.data);
      return undefined;
    }

    let cancelled = false;

    if (
      shouldSkipEmptyAudienceUserSearch(
        allowOrganisationWideAudienceSearch,
        audience?.predicates,
      )
    ) {
      setCommunityResolutionUsers([]);
      return undefined;
    }

    const query = Api.utils.toQuery({
      offset: 0,
      limit: total,
      include: ['networks', 'functions'],
      q: audience?.search,
    });

    Api.post<PredicateFilterApiResponse>(
      `/v1/organisations/${organisation.id}/users/search?${query}`,
      {
        predicate_type: audience?.predicate_type,
        predicates: predicatesToPayload(audience?.predicates),
      },
    ).then((result) => {
      if (!cancelled) setCommunityResolutionUsers(result.data);
    });

    return () => {
      cancelled = true;
    };
  }, [
    allowOrganisationWideAudienceSearch,
    audience,
    isCommunityAudienceMode,
    organisation.id,
    searchResult,
  ]);

  // @ts-expect-error
  const AudienceModal = React.memo(({ children, show }: AudienceModalProps) => {
    const [audienceSearch, setAudienceSearch] = React.useState<string | null>(null);
    const [scrollContainer, setScrollContainer] = React.useState<HTMLDivElement | void>();
    const { minHeight: contentMinHeight, ref: contentRef } = useStoredContentHeight(show);

    const handleOnShow = () => {
      if (document) {
        // @ts-expect-error
        setScrollContainer(document.querySelector('.AudienceModal .modal-body'));
      }
    };

    return (
      <Modal
        size="large"
        title={t(audienceSearchResultI18nKeys.modalTitle)}
        show={show}
        onShow={handleOnShow}
        onHide={() => {
          setShowAudienceModal(false);
          setAudienceSearch(null);
        }}
        className="AudienceModal"
        headerContent={(
          // @ts-expect-error
          <SearchBar defaultValue={audienceSearch} onSearch={setAudienceSearch} />
        )}
        content={(
          <div ref={contentRef as React.RefObject<HTMLDivElement>} style={{ minHeight: contentMinHeight }}>
            {show && isCommunityAudienceMode && (
              // A per-community task opens one instance per community, so the
              // list behind "View all" is the communities, not their members —
              // already resolved, so it filters in memory rather than refetching.
              <Table
                scrollContainer={scrollContainer as HTMLDivElement}
                columns={communityColumns}
                items={filterAudienceCommunitiesBySearch(audienceCommunities, audienceSearch)}
                renderRow={PredicatesCommunityItem}
                placeholder={(
                  <div className="Placeholder">
                    <div className="Placeholder__Inner">
                      <Trans i18nKey={audienceSearchResultI18nKeys.placeholder} />
                    </div>
                  </div>
                )}
              />
            )}
            {show && !isCommunityAudienceMode ? (
              <AsyncTable
                key={`${searchResult?.meta?.pagination?.total_count ?? 0}-${searchResult?.data?.length ?? 0}`}
                // @ts-expect-error
                scrollContainer={scrollContainer}
                columns={columns}
                data={{
                  onFetch,
                  limit: 25,
                  cache: searchResult ? {
                    items: searchResult.data,
                    pagination: searchResult.meta.pagination,
                  } : undefined,
                  filter: buildAudienceModalFilter(audience, audienceSearch),
                }}
                renderRow={PredicatesUserItem}
                rowProps={{
                  maxShownItems: 2,
                  baseUrl,
                }}
              />
            ) : null}
          </div>
        )}
      >
        {children}
      </Modal>
    );
  });

  return (
    <div className="PredicatesFilter">
      {/* @ts-expect-error */}
      <AudienceModal show={showAudienceModal} />
      <div className="PredicatesFilter__Column PredicatesFilter__Column--size-large">
        {!hideLabel && (
          <Label text={t('common:predicates_filter_who_will_receive')} description={tooltips.audience} />
        )}

        <div className="PredicatesFilter__Filters">
          <Field
            name={`${name}.predicate_type`}
            // @ts-expect-error
            component={({ input: { value, onChange } }) => (
              <Dropdown
                small
                disabled={readOnly}
                toggle={(
                  <Button size="large" disabled={readOnly}>
                    <Trans i18nKey="common:predicates_filter_type" values={{ context: value }} />
                  </Button>
                )}
              >
                {predicateTypes.map((type) => (
                  <Dropdown.Item key={type} onClick={() => onChange(type)} selected={value === type}>
                    <Trans i18nKey="common:predicates_filter_type" values={{ context: type }} />
                  </Dropdown.Item>
                ))}
              </Dropdown>
            )}
          />
          {/* @ts-ignore */}
          <FieldArray
            name={`${name}.predicates`}
            // @ts-ignore
            predicateType={audience.predicate_type}
            data={data}
            enabledFilters={enabledFilters}
            customPropertyFilters={customPropertyFilters}
            userPropertyFilters={userPropertyFilters}
            organisation={organisation}
            getState={getState}
            searchResult={searchResult}
            onAddFilterClick={onAddFilterClick}
            onFilterSelect={onFilterSelect}
            addContextToPredicatesOnChange={addContextToPredicatesOnChange}
            readOnly={readOnly}
            lockedFilters={lockedFilters}
            component={PredicatesList}
          />
        </div>

        {fieldError && (
          <div className="tw-text-red-500 tw-my-2 tw-text-base" role="alert">
            {fieldError}
          </div>
        )}

        {searchResult && (
          <h3>
            <Trans
              i18nKey={audienceSearchResultI18nKeys.result}
              count={audienceSearchResultCount}
              values={{ count: audienceSearchResultCount }}
              components={[<b />]}
            />
            &nbsp;·&nbsp;
            <a onClick={() => setShowAudienceModal(true)}>
              <Trans i18nKey="common:predicates_filter_view_all" />
            </a>
          </h3>
        )}

        {description && <div className="PredicatesFilter__Description">{description}</div>}
        <div
          ref={inlineContentRef as React.RefObject<HTMLDivElement>}
          className={hideResultsTable ? 'tw-hidden' : undefined}
          style={hideResultsTable ? undefined : { minHeight: inlineMinHeight }}
        >
          {/* @ts-expect-error */}
          <AsyncTable
            columns={columns}
            data={{
              onFetch,
              onPostFetch: (result: PredicateFilterApiResponse) => {
                setSearchResult(result);
                getSearchResults?.(result);
              },
              filter: audience,
            }}
            rowProps={{
              baseUrl,
            }}
            renderRow={PredicatesUserItem}
            ShowMoreComponent={({ count }: { count: number }) => (
              <div className="PredicatesFilter__ShowMore" onClick={() => setShowAudienceModal(true)}>
                <Trans i18nKey="common:predicates_filter_view_all_employees" values={{ count }} />
              </div>
            )}
            placeholder={(
              <div className="Placeholder">
                <div className="Placeholder__Inner">
                  <Trans i18nKey={audienceSearchResultI18nKeys.placeholder} />
                </div>
              </div>
            )}
          />
        </div>
      </div>
    </div>
  );
};

export const PredicatesFilter = predicatesFilterConnector(PredicatesFilterComponent);


